import React, {memo, useLayoutEffect, useRef, useState} from 'react';
import classNames from 'classnames';
import {observer} from "mobx-react-lite";
import {isAlive} from 'mobx-state-tree';

import type {FullTextSearchResult} from "@stores/SearchStore";

import {useAppState, useRootStore, useUserState} from "@stores/AppStateContext";
import {useCrudServices} from "../../../platform/hooks/useServiceCombinations";

import {selectItemPlain} from "../../../actions/ui/selectItem";
import {ItemModelType} from "@stores/WorkspaceStore";
import {ItemTrashMenuConfirmation} from "./ItemTrashMenuConfirmation";

import {StarIconFilled} from "../../components/icons/StarIconFilled";
import {ExpandIcon} from "../../components/icons/ExpandIcon";
import {SearchContext} from "./SearchContext";

import {ItemButtons} from './ItemButtons';
import {isPinned} from "../../../utils/isPinned";
import {clearAllTempState} from "../../../actions/ui/clearAllTempState";
import {highlightText} from "../../../utils/highlightText";
import {ItemLineActive} from "./ItemLineActive";
import {pinItemUndoable, unPinItemUndoable} from "../../../actions/item/pin";
import {ItemState} from "../../components/Tree/types";

import {indentPerLevel} from "../../components/Tree/constants";


import './ItemLine.css';


const ICON_SIZE = 16;

export interface ItemLineProps {
    state?: ItemState
    level: number;
    item: ItemModelType;
    index?: number;
    inEditorView?: boolean;
    searchResult?: FullTextSearchResult;
    highlight?: string;
    subtitle?: string | null;
    onContextMenu?: (event: React.MouseEvent) => void
    parentsHash?: string | null;
    parentIdFromTree?: string | null;
}

export const ItemLine = memo(observer<ItemLineProps>(
    ({
            item,
            parentIdFromTree,
            level,
            state = 'idle',
            inEditorView = false,
            searchResult,
            highlight,
            subtitle,
            parentsHash,
            index,
            ...props
        }
    ) => {
        const appState = useAppState();
        const rootStore = useRootStore();
        const userState = useUserState();
        const crudServices = useCrudServices();
        const itemLineRef = useRef<HTMLDivElement | null>(null);
        const activeWorkspace = rootStore.view.workspace!;
        const isSelected = activeWorkspace.selectedId === item.id;
        const [isMoreClicked, setMoreClicked] = useState(false);

        const showExtendIcon = item.isCluster && !searchResult;
        const isReader = userState!.isCurrentUserReadOnly;

        // scroll into view
        useLayoutEffect(() => {
            if (activeWorkspace.scrollIntoViewId === item.id && itemLineRef.current) {
                itemLineRef.current.scrollIntoView();
            }
            return () => {
                /* item can be detached if we undo duplicate actions */
                if (isAlive(item) && activeWorkspace.confirmDeleteOpenUnder) {
                    activeWorkspace.setConfirmDeleteOpen(undefined);
                }
            }
        }, [itemLineRef, activeWorkspace, item]);

        const handleExpandToggle = (event: React.SyntheticEvent) => {
            console.log('handleExpandToggle', item.isExpanded);
            item.setExpanded(!item.isExpanded);
            event.stopPropagation();
        };

        const handlePinToggle = (event: React.SyntheticEvent) => {
            if (item.pinned) {
                unPinItemUndoable(crudServices, userState!.dsManager!, item.id, activeWorkspace)
            } else {
                pinItemUndoable(crudServices, userState!.dsManager!, item.id, activeWorkspace)
            }
        };

        const handleSelect = () => {
            if (isSelected) {
                if (appState.mobileNavController.isMobile && !inEditorView) {
                    appState.mobileNavController.openPage();
                } else {
                    item.setExpanded(!item.isExpanded);
                }
            } else {
                selectItemPlain(rootStore, activeWorkspace, item.id);
            }
            if (!showConfirmDeleteDialog) {
                clearAllTempState(rootStore);
            }
        };

        const _isPinned = !searchResult && level === 0 && isPinned(item);
        const statusSubtitle = typeof subtitle === 'string' && subtitle.trim().length > 0
            ? subtitle
            : null;
        const showStatusSubtitle = !!statusSubtitle && !searchResult && !highlight;

        let paddingLeft = level * indentPerLevel;

        if (!showExtendIcon) {
            paddingLeft = paddingLeft + ICON_SIZE;
        }

        const showConfirmDeleteDialog = (
            activeWorkspace.confirmDeleteOpenUnder !== undefined
            && activeWorkspace.confirmDeleteOpenUnder === `${parentIdFromTree}/${item.id}`
        );

        const _itemName = item.getNameWithPlaceholder(rootStore.currentLocale);

        return (
            <>
                <div
                    key={`${parentsHash}-${item.id}`}
                    {...props}
                    ref={itemLineRef}
                    className={classNames({
                        'ItemLine title-small': true,
                        'ItemLine--inEditorView': inEditorView,
                        'ItemLine--selected': isSelected,
                        'ItemLine--search': searchResult || highlight,
                        'ItemLine--dragging': state === "dragging",
                        'ItemLine--drag-over': state === 'parent-of-instruction',
                        'ItemLine--confirm-delete': showConfirmDeleteDialog,
                        'ItemLine--isCluster': item.isCluster,
                        'ItemLine--withSubtitle': showStatusSubtitle,
                    })}
                    onClick={handleSelect}
                    onContextMenu={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (!isReader) {
                            appState.addAdditionalContextMenu({
                                    clientX: event.clientX,
                                    clientY: event.clientY,
                                    type: 'item',
                                    id: item.id,
                                    params: {
                                        itemId: item.id,
                                        parentIdFromTree: parentIdFromTree,
                                        inEditorView: inEditorView,
                                        index: index,
                                    }
                                }
                            );
                        }
                    }}
                >
                    <div
                        className={classNames({
                            'ItemLine__row': true,
                            'ItemLine__row-Reader': isReader,
                            'ItemLine__row-moreClicked': isMoreClicked,
                        })}
                        style={{paddingLeft: paddingLeft}}
                    >
                        <div className='ItemLine__block'>
                            <div className='ItemLine__block-main'>
                                {showExtendIcon
                                    ? <ExpandIcon onClick={handleExpandToggle} size={ICON_SIZE}
                                                  isExpanded={item.isExpanded}/>
                                    : null
                                }
                                {_isPinned
                                    ? <StarIconFilled className='ItemLine_pin' onClick={handlePinToggle}
                                                      size={ICON_SIZE}/>
                                    : null
                                }
                                <span
                                    key={rootStore.currentLocale}
                                    className='ItemLine__text'
                                    dangerouslySetInnerHTML={{'__html': highlight ? highlightText(highlight, _itemName) : _itemName}}
                                />
                            </div>
                            {showStatusSubtitle ? (
                                <div className={classNames({
                                    'ItemLine__subtitle label-small': true,
                                    'ItemLine__subtitle--withExpand': showExtendIcon,
                                })} title={statusSubtitle}>
                                    {statusSubtitle}
                                </div>
                            ) : null}
                            {!!searchResult ? <SearchContext searchResult={searchResult}/> : null}
                        </div>
                        <ItemButtons
                            key={`${parentIdFromTree}-${item.id}`}
                            item={item}
                            parentIdFromTree={parentIdFromTree}
                            index={index}
                            isInSearch={!!(searchResult || highlight)}
                            inEditorView={inEditorView}
                            isMoreClicked={isMoreClicked}
                            setMoreClicked={setMoreClicked}
                        />
                        <ItemLineActive
                            item={item}
                        />
                    </div>
                </div>
                <div>
                    {
                        showConfirmDeleteDialog
                            ? <ItemTrashMenuConfirmation item={item} parentIdFromTree={parentIdFromTree}
                                                         className={'ItemLine_delete'}/>
                            : null
                    }
                </div>
            </>
        )
    }
))
